import React, { useMemo, useState } from "react";
import { FiMail, FiPhone, FiCalendar, FiMessageSquare, FiUser, FiTag, FiCheck, FiClock, FiDollarSign, FiArchive, FiTrash2, FiEdit3 } from "react-icons/fi";

// import SectionHeader from "../components/ui/SectionHeader";
import StatCard from "../components/ui/StatCard";
import { useGetContactSubmissionsQuery, useGetContactStatsQuery, useUpdateContactSubmissionMutation, useDeleteContactSubmissionMutation } from "../services/api";
import { shortDate } from "../utils/formatters";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "#3b82f6", icon: <FiClock /> },
  { value: "contacted", label: "Contacted", color: "#8b5cf6", icon: <FiPhone /> },
  { value: "quoted", label: "Quoted", color: "#f59e0b", icon: <FiDollarSign /> },
  { value: "converted", label: "Converted", color: "#10b981", icon: <FiCheck /> },
  { value: "archived", label: "Archived", color: "#6b7280", icon: <FiArchive /> },
];

const StatusBadge = ({ status }) => {
  const statusObj = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 12px",
        borderRadius: "12px",
        fontSize: "0.8125rem",
        fontWeight: 600,
        background: `${statusObj.color}20`,
        color: statusObj.color,
      }}
    >
      {statusObj.icon}
      {statusObj.label}
    </span>
  );
};

const ContactSubmissionsPage = () => {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [editStatus, setEditStatus] = useState("");

  const { data, isLoading } = useGetContactSubmissionsQuery();
  const { data: statsData } = useGetContactStatsQuery();
  const [updateSubmission, { isLoading: isUpdating }] = useUpdateContactSubmissionMutation();
  const [deleteSubmission, { isLoading: isDeleting }] = useDeleteContactSubmissionMutation();

  const submissions = useMemo(() => data?.data || [], [data]);
  const stats = useMemo(() => statsData?.data || {}, [statsData]);

  const filteredSubmissions = useMemo(() => {
    if (selectedStatus === "all") return submissions;
    return submissions.filter((sub) => sub.status === selectedStatus);
  }, [submissions, selectedStatus]);

  const handleUpdateSubmission = async () => {
    if (!selectedSubmission) return;

    try {
      await updateSubmission({
        id: selectedSubmission.id,
        status: editStatus || selectedSubmission.status,
        notes: editNotes || selectedSubmission.notes,
      }).unwrap();

      setSelectedSubmission(null);
      setEditNotes("");
      setEditStatus("");
    } catch (error) {
      console.error("Failed to update submission:", error);
      alert("Failed to update submission. Please try again.");
    }
  };

  const handleDeleteSubmission = async (id) => {
    if (!window.confirm("Are you sure you want to delete this contact submission?")) return;

    try {
      await deleteSubmission(id).unwrap();
    } catch (error) {
      console.error("Failed to delete submission:", error);
      alert("Failed to delete submission. Please try again.");
    }
  };

  const openEditModal = (submission) => {
    setSelectedSubmission(submission);
    setEditStatus(submission.status);
    setEditNotes(submission.notes || "");
  };

  const closeEditModal = () => {
    setSelectedSubmission(null);
    setEditNotes("");
    setEditStatus("");
  };

  if (isLoading) {
    return <div className="loading-state">Loading contact submissions...</div>;
  }

  return (
    <div className="dashboard-page">
      {/* <SectionHeader
        eyebrow="Contact Submissions"
        title="Manage website contact form submissions"
        description="View and track quote requests and inquiries from potential customers"
      /> */}

      {/* Statistics */}
      <div className="stats-grid">
        <StatCard
          label="Total Submissions"
          value={stats.total || 0}
          tone="default"
          helper={`${stats.today_count || 0} today`}
        />
        <StatCard
          label="New Inquiries"
          value={stats.new_count || 0}
          tone="info"
          helper="Awaiting response"
        />
        <StatCard
          label="This Week"
          value={stats.week_count || 0}
          tone="default"
          helper="Last 7 days"
        />
        <StatCard
          label="Converted"
          value={stats.converted_count || 0}
          tone="success"
          helper="Became customers"
        />
      </div>

      {/* Status Filter */}
      <div className="panel" style={{ marginBottom: "1.5rem", padding: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <FiTag style={{ color: "var(--brand, #e8702a)" }} />
          <strong style={{ fontSize: "0.875rem" }}>Filter by status:</strong>
          <button
            className={selectedStatus === "all" ? "btn-primary" : "btn-secondary"}
            type="button"
            onClick={() => setSelectedStatus("all")}
            style={{ fontSize: "0.875rem", padding: "6px 16px" }}
          >
            All ({submissions.length})
          </button>
          {STATUS_OPTIONS.map((statusOpt) => {
            const count = submissions.filter((s) => s.status === statusOpt.value).length;
            return (
              <button
                key={statusOpt.value}
                className={selectedStatus === statusOpt.value ? "btn-primary" : "btn-secondary"}
                type="button"
                onClick={() => setSelectedStatus(statusOpt.value)}
                style={{
                  fontSize: "0.875rem",
                  padding: "6px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {statusOpt.icon}
                {statusOpt.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Submissions Table */}
      <div className="panel" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid var(--border, #e5e7eb)" }}>
          <h3 style={{ margin: 0 }}>
            Contact Submissions ({filteredSubmissions.length})
          </h3>
        </div>
        <div className="table-wrap">
          {filteredSubmissions.length === 0 ? (
            <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted, #888)" }}>
              <FiMail size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
              <p style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "8px" }}>
                No submissions found
              </p>
              <p style={{ fontSize: "0.875rem" }}>
                {selectedStatus !== "all"
                  ? `No submissions with status: ${STATUS_OPTIONS.find((s) => s.value === selectedStatus)?.label}`
                  : "No contact form submissions yet"}
              </p>
            </div>
          ) : (
            <table className="data-table" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Service</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map((submission) => (
                  <tr key={submission.id}>
                    <td style={{ whiteSpace: "nowrap", fontSize: "0.875rem" }}>
                      {shortDate(submission.created_at)}
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <FiUser style={{ color: "var(--brand, #e8702a)" }} />
                        <strong style={{ fontSize: "0.875rem" }}>{submission.name}</strong>
                      </div>
                    </td>
                    <td style={{ fontSize: "0.8125rem" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <FiMail size={12} />
                          <a href={`mailto:${submission.email}`} style={{ color: "inherit" }}>
                            {submission.email}
                          </a>
                        </div>
                        {submission.phone && (
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <FiPhone size={12} />
                            <a href={`tel:${submission.phone}`} style={{ color: "inherit" }}>
                              {submission.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: "0.875rem" }}>
                      {submission.service || "—"}
                    </td>
                    <td style={{ maxWidth: "300px", fontSize: "0.875rem" }}>
                      <div style={{ marginBottom: "4px" }}>
                        {submission.message.length > 100
                          ? `${submission.message.substring(0, 100)}...`
                          : submission.message}
                      </div>
                      {submission.deadline && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            color: "var(--text-muted, #888)",
                            fontSize: "0.75rem",
                          }}
                        >
                          <FiCalendar size={12} />
                          Deadline: {submission.deadline}
                        </div>
                      )}
                    </td>
                    <td>
                      <StatusBadge status={submission.status} />
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                        <button
                          className="btn-secondary"
                          type="button"
                          onClick={() => openEditModal(submission)}
                          style={{
                            padding: "6px 12px",
                            fontSize: "0.8125rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <FiEdit3 size={14} /> Edit
                        </button>
                        <button
                          className="btn-secondary"
                          type="button"
                          onClick={() => handleDeleteSubmission(submission.id)}
                          disabled={isDeleting}
                          style={{
                            padding: "6px 12px",
                            fontSize: "0.8125rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            color: "#dc2626",
                          }}
                        >
                          <FiTrash2 size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {selectedSubmission && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={closeEditModal}
        >
          <div
            className="panel"
            style={{
              maxWidth: "600px",
              width: "100%",
              padding: "24px",
              maxHeight: "90vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
              <FiMessageSquare style={{ color: "var(--brand, #e8702a)" }} />
              Edit Contact Submission
            </h2>

            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                <div>
                  <strong style={{ fontSize: "0.875rem", color: "var(--text-muted, #888)" }}>Name</strong>
                  <p style={{ margin: "4px 0 0", fontSize: "0.9375rem" }}>{selectedSubmission.name}</p>
                </div>
                <div>
                  <strong style={{ fontSize: "0.875rem", color: "var(--text-muted, #888)" }}>Date</strong>
                  <p style={{ margin: "4px 0 0", fontSize: "0.9375rem" }}>{shortDate(selectedSubmission.created_at)}</p>
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <strong style={{ fontSize: "0.875rem", color: "var(--text-muted, #888)" }}>Email</strong>
                <p style={{ margin: "4px 0 0", fontSize: "0.9375rem" }}>
                  <a href={`mailto:${selectedSubmission.email}`}>{selectedSubmission.email}</a>
                </p>
              </div>

              {selectedSubmission.phone && (
                <div style={{ marginBottom: "16px" }}>
                  <strong style={{ fontSize: "0.875rem", color: "var(--text-muted, #888)" }}>Phone</strong>
                  <p style={{ margin: "4px 0 0", fontSize: "0.9375rem" }}>
                    <a href={`tel:${selectedSubmission.phone}`}>{selectedSubmission.phone}</a>
                  </p>
                </div>
              )}

              {selectedSubmission.service && (
                <div style={{ marginBottom: "16px" }}>
                  <strong style={{ fontSize: "0.875rem", color: "var(--text-muted, #888)" }}>Service</strong>
                  <p style={{ margin: "4px 0 0", fontSize: "0.9375rem" }}>{selectedSubmission.service}</p>
                </div>
              )}

              {selectedSubmission.deadline && (
                <div style={{ marginBottom: "16px" }}>
                  <strong style={{ fontSize: "0.875rem", color: "var(--text-muted, #888)" }}>Deadline</strong>
                  <p style={{ margin: "4px 0 0", fontSize: "0.9375rem" }}>{selectedSubmission.deadline}</p>
                </div>
              )}

              <div style={{ marginBottom: "16px" }}>
                <strong style={{ fontSize: "0.875rem", color: "var(--text-muted, #888)" }}>Message</strong>
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: "0.9375rem",
                    padding: "12px",
                    background: "var(--bg-subtle, #f9fafb)",
                    borderRadius: "6px",
                    border: "1px solid var(--border, #e5e7eb)",
                  }}
                >
                  {selectedSubmission.message}
                </p>
              </div>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, fontSize: "0.875rem" }}>
                Status
              </label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "2px solid var(--border, #e5e7eb)",
                  borderRadius: "6px",
                  fontSize: "0.9375rem",
                }}
              >
                {STATUS_OPTIONS.map((statusOpt) => (
                  <option key={statusOpt.value} value={statusOpt.value}>
                    {statusOpt.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, fontSize: "0.875rem" }}>
                Internal Notes
              </label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Add internal notes about this submission..."
                rows="4"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "2px solid var(--border, #e5e7eb)",
                  borderRadius: "6px",
                  fontSize: "0.9375rem",
                  fontFamily: "inherit",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                className="btn-secondary"
                type="button"
                onClick={closeEditModal}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                type="button"
                onClick={handleUpdateSubmission}
                disabled={isUpdating}
              >
                {isUpdating ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSubmissionsPage;
